import { useEffect, useMemo, useState } from 'react';
import { useChangeTick } from '../lib/changes';
import { api, quarterLabel, slotLabel, type Grid, type GridCell, type GridCoverage } from '../lib/api';
import { addDays, dayLabel, monthLabel, monthOf } from '../lib/dates';
import { navigate } from '../lib/router';

/**
 * The quarter grid, mirroring OSMD's own Scheduling Grid (D17): providers
 * down the side, Tuesday-to-Tuesday weeks across, a site letter in each
 * cell, coverage counts per site under the rows, request notes to the right.
 * Click a cell or a coverage count to open that site's week.
 */
export function AdminGrid({ quarter }: { quarter: string }) {
  const [grid, setGrid] = useState<Grid | null>(null);
  const [error, setError] = useState<string | null>(null);
  const changes = useChangeTick();

  useEffect(() => {
    setGrid(null);
    setError(null);
    api.grid(quarter).then(setGrid).catch((e: Error) => setError(e.message));
  }, [quarter]);
  useEffect(() => {
    if (changes) api.grid(quarter).then(setGrid).catch((e: Error) => setError(e.message));
  }, [changes]);

  const cellsBy = useMemo(() => {
    const m = new Map<string, GridCell[]>();
    for (const c of grid?.cells ?? []) m.set(`${c.physicianId}|${c.week}`, [...(m.get(`${c.physicianId}|${c.week}`) ?? []), c]);
    return m;
  }, [grid]);
  const coverageBy = useMemo(() => new Map((grid?.coverage ?? []).map((c) => [`${c.siteId}|${c.week}`, c])), [grid]);
  const availBy = useMemo(() => new Map((grid?.availability ?? []).map((a) => [`${a.physicianId}|${a.week}`, a.kind])), [grid]);
  // Hospitals worth a coverage row: those with a period in the quarter, plus any the engine can
  // build — otherwise an all-empty quarter offers no way in to the builder at all (D42).
  const sitesWithData = useMemo(
    () => (grid?.sites ?? []).filter((s) => s.engineSupported || grid!.coverage.some((c) => c.siteId === s.id && c.periodStart)),
    [grid],
  );
  // Rows shown by default: anyone with a shift this quarter, or who has sent in a request for it.
  const rowsWithShifts = useMemo(() => (grid?.physicians ?? []).filter((p) => p.responded || grid!.weeks.some((w) => cellsBy.has(`${p.abbreviation}|${w}`))), [grid, cellsBy]);
  const rowsWithout = useMemo(() => (grid?.physicians ?? []).filter((p) => !rowsWithShifts.includes(p)), [grid, rowsWithShifts]);
  const [showAll, setShowAll] = useState(false);

  const open = (siteId: string, week: string) => {
    const cov = coverageBy.get(`${siteId}|${week}`);
    if (cov?.periodStart) navigate(`/admin/sites/${siteId}/periods/${cov.periodStart}`);
  };

  if (error) return <p className="error">Couldn't load the grid ({error}).</p>;
  if (!grid) return <p className="muted">Loading {quarterLabel(quarter)}…</p>;

  const rows = showAll ? grid.physicians : rowsWithShifts;
  return (
    <section className="gridpage">
      <div className="gridtools">
        <QuarterPicker value={quarter} onChange={(q) => navigate(`/admin/grid?quarter=${q}`)} />
        <span className="muted">{grid.cells.length} assignments · {plural(grid.physicians.filter((p) => p.responded).length, 'request')} in · {rowsWithShifts.length} of {grid.physicians.length} physicians shown</span>
        {rowsWithout.length > 0 && (
          <button className="linklike" type="button" onClick={() => setShowAll((v) => !v)}>
            {showAll ? 'hide' : 'show'} the {rowsWithout.length} with neither shifts nor a request
          </button>
        )}
        <button className="linklike" type="button" onClick={() => navigate(`/admin/requests?quarter=${quarter}`)}>requests</button>
        <button className="linklike" type="button" onClick={() => navigate(`/admin/draft?quarter=${quarter}`)} title="Draft this quarter and review it — you choose what to keep and what to accept">draft this quarter</button>
      </div>
      <div className="gridscroll">
        <table className="grid">
          <thead>
            <tr>
              <th className="sticky name">Provider</th>
              {grid.weeks.map((w, i) => (
                <th key={w} className="week">
                  <div className="wk-days">{weekHeader(w)}</div>
                  <div className="wk-month">wk {i + 1}</div>
                </th>
              ))}
              <th className="notes">Requests / notes</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((p) => (
              <tr key={p.abbreviation}>
                <th className="sticky name" title={p.abbreviation}>
                  {[p.lastName, p.firstName].filter(Boolean).join(', ')}
                  {p.status !== 'Active' && <span className="status">{p.status}</span>}
                </th>
                {grid.weeks.map((w) => {
                  const cells = cellsBy.get(`${p.abbreviation}|${w}`) ?? [];
                  const asked = availBy.get(`${p.abbreviation}|${w}`);
                  return (
                    <td key={w} className={`cell${asked ? ` ${asked}` : ''}`} title={asked === 'want' ? `${p.lastName} asked for this week` : asked === 'blackout' ? `${p.lastName} is not available this week` : undefined}>
                      {cells.map((c) => (
                        <button
                          key={c.siteId}
                          type="button"
                          className={`chip site-${c.siteId}${c.days < 2 ? ' partial' : ''}`}
                          title={`${siteName(grid, c.siteId)} — ${c.slots.map(slotLabel).join(', ')} — ${c.days} day${c.days === 1 ? '' : 's'}`}
                          onClick={() => open(c.siteId, w)}
                        >
                          {cellText(grid, c)}
                        </button>
                      ))}
                    </td>
                  );
                })}
                <td className="notes">
                  {p.requestNote && <span className="reqnote">{p.requestNote}</span>}
                  {p.requestNote && p.notes && <br />}
                  {p.notes && <span className="muted">{p.notes}</span>}
                </td>
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr className="foot-head">
              <th className="sticky name">Coverage</th>
              {grid.weeks.map((w) => <th key={w} />)}
              <th />
            </tr>
            {sitesWithData.map((s) => (
              <tr key={s.id}>
                <th className="sticky name">
                  <span className={`letter site-${s.id}`}>{s.letter}</span> {s.name}
                </th>
                {grid.weeks.map((w) => {
                  const c = coverageBy.get(`${s.id}|${w}`);
                  return (
                    <td key={w} className="cell">
                      <CoverageCell c={c} site={s} week={w} onOpen={() => open(s.id, w)} />
                    </td>
                  );
                })}
                <td className="notes muted">providers on a full stint / daily positions expected · open days</td>
              </tr>
            ))}
          </tfoot>
        </table>
      </div>
      <p className="legend">
        {grid.sites.map((s) => (
          <span key={s.id}>
            <span className={`letter site-${s.id}`}>{s.letter}</span> {s.name}{' '}
          </span>
        ))}
        <span className="muted">· cells show site letter + position (rounder number; N night, D day, S swing…); faded = handoff day only · <span className="swatch want" /> asked for the week · <span className="swatch blackout" /> can't work it · click any cell or coverage count to open that week</span>
      </p>
    </section>
  );
}

function CoverageCell({ c, site, week, onOpen }: { c: GridCoverage | undefined; site: { id: string; engineSupported: boolean }; week: string; onOpen: () => void }) {
  if (!c || !c.periodStart) {
    // Nothing here yet. Where the engine knows the hospital's shape, this is where a week gets built (D42).
    return site.engineSupported
      ? <button type="button" className="cov build" title="Nothing scheduled — build this week" onClick={() => navigate(`/admin/sites/${site.id}/periods/${week}/build`)}>build</button>
      : <span className="muted">—</span>;
  }
  if (c.expected === 0) {
    // No daily-position shape configured for this site yet: we can count people, not gaps.
    return <button type="button" className="cov" onClick={onOpen} title="Expected positions not configured for this site — showing providers on a full stint">{c.providers}</button>;
  }
  const short = c.providers < c.expected;
  const cls = c.open > 0 ? 'cov open' : short ? 'cov short' : 'cov ok';
  return (
    <button type="button" className={cls} onClick={onOpen} title={c.open ? `${c.open} expected slot-days have nobody` : 'fully covered'}>
      {c.providers}/{c.expected}
      {c.open > 0 && <span className="openbadge">{c.open}</span>}
    </button>
  );
}

function QuarterPicker({ value, onChange }: { value: string; onChange: (q: string) => void }) {
  const [y, q] = value.split('-Q').map(Number);
  const step = (n: number) => {
    const idx = y * 4 + (q - 1) + n;
    onChange(`${Math.floor(idx / 4)}-Q${(idx % 4) + 1}`);
  };
  return (
    <div className="quarterpicker">
      <button type="button" onClick={() => step(-1)} aria-label="Previous quarter">‹</button>
      <h1>{quarterLabel(value)}</h1>
      <button type="button" onClick={() => step(1)} aria-label="Next quarter">›</button>
    </div>
  );
}

/** "Jan 5–12" (Tuesday to the handoff Tuesday), or "Dec 29 – Jan 5" across a month boundary. */
function weekHeader(tuesday: string): string {
  const end = addDays(tuesday, 7);
  const startText = dayLabel(tuesday).replace(/^\w+, /, '');
  const endDay = end.slice(8, 10).replace(/^0/, '');
  return monthOf(tuesday) === monthOf(end) ? `${startText}–${endDay}` : `${startText} – ${monthLabel(monthOf(end)).split(' ')[0].slice(0, 3)} ${endDay}`;
}

function siteName(grid: Grid, siteId: string): string {
  return grid.sites.find((s) => s.id === siteId)?.name ?? siteId;
}

/** Short codes for non-rounder positions so a cell stays a few characters wide. */
const SHORT: Record<string, string> = { night: 'N', 'night-am': 'N', daytime: 'D', swing: 'S', pulmcrit: 'PC', cardiology: 'C', 'trauma-surgery': 'TS', 'night-first-call': 'FC', 'backup-night-md': 'B' };

function cellText(grid: Grid, c: GridCell): string {
  const letter = grid.sites.find((s) => s.id === c.siteId)?.letter ?? '?';
  const rounders = c.slots.map((s) => s.match(/^rounder-(\d)$/)?.[1]).filter(Boolean);
  const specials = c.slots.filter((s) => !/^rounder-\d$/.test(s) && s !== 'night-backup').map((s) => SHORT[s] ?? slotLabel(s).slice(0, 2));
  const suffix = rounders.length ? rounders.join('/') : specials.join('/');
  return `${letter}${suffix}`;
}

function plural(n: number, noun: string): string {
  return `${n} ${noun}${n === 1 ? '' : 's'}`;
}
